import React, { useState, useEffect } from 'react';
import { 
  SparklesIcon, 
  TagIcon, 
  LightBulbIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { AITagSuggestion as AITagSuggestionType } from '../types/DocumentTypes';

interface AITagSuggestionProps {
  documentTitle: string;
  documentContent?: string;
  selectedTags: string[];
  onTagsSuggested: (tags: string[]) => void;
  onTagAdd: (tag: string) => void;
  disabled?: boolean;
}

export const AITagSuggestion: React.FC<AITagSuggestionProps> = ({
  documentTitle,
  documentContent,
  selectedTags,
  onTagsSuggested,
  onTagAdd,
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState<AITagSuggestionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Generate AI suggestions when title or content changes
  useEffect(() => {
    if (documentTitle.trim() && !disabled) {
      generateSuggestions();
    }
  }, [documentTitle, documentContent]);

  const generateSuggestions = async () => {
    if (!documentTitle.trim()) return;

    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call to AI service
      // const response = await fetch('/api/v1/dms/ai/tag-suggestions', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ title: documentTitle, content: documentContent })
      // });
      // const data = await response.json();

      // Mock AI suggestions for now
      const mockSuggestions: AITagSuggestionType[] = generateMockSuggestions(documentTitle);
      
      // Filter out already selected tags
      const filteredSuggestions = mockSuggestions.filter(
        suggestion => !selectedTags.includes(suggestion.tag)
      );

      setSuggestions(filteredSuggestions);
      setShowSuggestions(true);
    } catch (err) {
      setError('Failed to generate AI suggestions');
      console.error('AI tag suggestion error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateMockSuggestions = (title: string): AITagSuggestionType[] => {
    const titleLower = title.toLowerCase();
    const suggestions: AITagSuggestionType[] = [];

    // Legal document type suggestions
    if (titleLower.includes('contract') || titleLower.includes('agreement')) {
      suggestions.push({
        tag: 'Contract',
        confidence: 0.95,
        category: 'legal',
        reasoning: 'Document title contains contract-related terms'
      });
    }

    if (titleLower.includes('brief') || titleLower.includes('motion')) {
      suggestions.push({
        tag: 'Legal Brief',
        confidence: 0.92,
        category: 'legal',
        reasoning: 'Document appears to be a legal brief or motion'
      });
    }

    if (titleLower.includes('discovery') || titleLower.includes('interrogatory')) {
      suggestions.push({
        tag: 'Discovery',
        confidence: 0.88,
        category: 'legal',
        reasoning: 'Document relates to discovery process'
      });
    }

    if (titleLower.includes('settlement') || titleLower.includes('resolution')) {
      suggestions.push({
        tag: 'Settlement',
        confidence: 0.85,
        category: 'legal',
        reasoning: 'Document appears to be settlement-related'
      });
    }

    // Business category suggestions
    if (titleLower.includes('business') || titleLower.includes('corporate')) {
      suggestions.push({
        tag: 'Business',
        confidence: 0.80,
        category: 'business',
        reasoning: 'Document appears to be business-related'
      });
    }

    // Technical category suggestions
    if (titleLower.includes('technical') || titleLower.includes('specification')) {
      suggestions.push({
        tag: 'Technical',
        confidence: 0.75,
        category: 'technical',
        reasoning: 'Document appears to be technical in nature'
      });
    }

    // Add some generic suggestions if we don't have many specific ones
    if (suggestions.length < 3) {
      suggestions.push(
        {
          tag: 'Documentation',
          confidence: 0.70,
          category: 'custom',
          reasoning: 'General document classification'
        },
        {
          tag: 'Important',
          confidence: 0.65,
          category: 'custom',
          reasoning: 'Document may be important based on context'
        }
      );
    }

    return suggestions.sort((a, b) => b.confidence - a.confidence);
  };

  const handleSuggestionClick = (suggestion: AITagSuggestionType) => {
    onTagAdd(suggestion.tag);
    // Remove the suggestion from the list
    setSuggestions(prev => prev.filter(s => s.tag !== suggestion.tag));
  };

  const handleAddAllSuggestions = () => {
    const newTags = suggestions.map(s => s.tag);
    onTagsSuggested(newTags);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600 bg-green-50 border-green-200';
    if (confidence >= 0.8) return 'text-lex-accent bg-lex-accent-soft border-lex-accent';
    if (confidence >= 0.7) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'legal': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'business': return 'text-lex-accent bg-lex-accent-soft border-lex-accent';
      case 'technical': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'custom': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (disabled) return null;

  return (
    <div className="space-y-3">
      {/* AI Suggestions Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-4 w-4 text-purple-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            AI Tag Suggestions
          </span>
          {loading && (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600"></div>
          )}
        </div>
        
        {suggestions.length > 0 && (
          <button
            onClick={handleAddAllSuggestions}
            className="text-xs text-purple-600 hover:text-purple-700 font-medium"
          >
            Add All
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
          {error}
        </div>
      )}

      {/* Suggestions List */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="space-y-2">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.tag}
              className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <TagIcon className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    {suggestion.tag}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full border ${getCategoryColor(suggestion.category)}`}>
                    {suggestion.category}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full border ${getConfidenceColor(suggestion.confidence)}`}>
                    {Math.round(suggestion.confidence * 100)}%
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {suggestion.reasoning && (
                  <div className="group relative">
                    <LightBulbIcon className="h-4 w-4 text-yellow-500 cursor-help" />
                    <div className="absolute bottom-full right-0 mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      {suggestion.reasoning}
                    </div>
                  </div>
                )}
                <CheckIcon className="h-4 w-4 text-green-600" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Suggestions Message */}
      {showSuggestions && suggestions.length === 0 && !loading && (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          <TagIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No AI suggestions available</p>
          <p className="text-xs">Try adding more content to get better suggestions</p>
        </div>
      )}

      {/* Generate Suggestions Button */}
      {!showSuggestions && !loading && (
        <button
          onClick={generateSuggestions}
          disabled={!documentTitle.trim()}
          className="w-full px-3 py-2 text-sm text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <SparklesIcon className="h-4 w-4" />
          Generate AI Suggestions
        </button>
      )}
    </div>
  );
}; 